import React from "react";
import { Component } from "react";
import { Form, InputGroup, Button } from 'react-bootstrap';
import { Definition, Entity, Property } from '../../data/Definition';
import { IItem, IMeasure } from "../../data/Query";
import { isEntity, isProperty, isStats } from "../../data/utils";

interface IMeasureCtrlProps {
  definition: Definition;
  measure: IMeasure | null;
  onUpdate: Function;
  title?: string;
}

interface IMeasureCtrlState {
    showConfig : boolean,
    isConfigValid: boolean,
    isEditing: Boolean,
    config: IMeasure,
    current: IMeasure | null
};

class MeasurePropertyCtrl extends Component<IMeasureCtrlProps, IMeasureCtrlState>  {
    private options: IItem[];

    constructor(props: any) {
        super(props);

        this.state = {
            showConfig: false,
            isConfigValid: false,
            isEditing: false,
            config: {} as IMeasure,                  
            current: this.props.measure
        };

        this.options = [];
    }

    componentDidMount() {
        this.initPropertyLists();
    }

    componentDidUpdate(prevProps:any){
        if(prevProps.measure !== this.props.measure){
            this.setState({          
                current: this.props.measure
            });
        }
    }

    initPropertyLists() {
        this.props.definition.getPropertiesOfType(['number', 'integer', 'stats']).forEach((property: Property) => {
            this.options.push({
                item: property,
                sub: null
            });
        });
    }

    render() {
        return (
            <Form.Group>
                <Form.Label>{this.props.title ? this.props.title : 'Measure (value to show)'}</Form.Label>
                { this.renderCurrent() }
                { this.renderEdit() }     
            </Form.Group>
        );
    }

    renderCurrent() {
        const {current} = this.state;

        return (
            this.state.showConfig ? 
                '' :
                <InputGroup className="mb-3">
                    <Form.Control 
                        type="text" 
                        value={current && current.source.item ? this.getMeasureSummary(current) : ''} 
                        readOnly
                        placeholder="add a measure"
                        onClick={this.onShow.bind(this)}
                    />

                    { 
                        current && current.source.item != null ?
                        <InputGroup.Append>
                            <Button 
                                variant="outline-secondary" 
                                onClick={this.onClear.bind(this)} 
                                title="Remove"
                            >X</Button>
                        </InputGroup.Append>
                        : ''
                    }
                </InputGroup>
        );
    }

    renderEdit() {
        const {config} = this.state;

        return (
            this.state.showConfig ?
                <div style={{backgroundColor: "silver", padding: "7px"}}>
                    <Form.Group>
                        <Form.Control as="select"
                            onChange={this.onItemChange.bind(this)}
                            defaultValue={config.source.item ? this.options.map((e:IItem) => { return e.item!.name; }).indexOf(config.source.item.name) : "-1"}>
                            <option value="-1">Select measure</option>
                            {this.options.map((item: any, i: number) => (
                                <option key={i} value={i}>{this.getItemDescription(item)}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    {
                        // value.
                        (config.source.item && isStats(config.source.item) || config.source.item && config.source.item.hasDate ) ?
                        <Form.Group>
                            <Form.Label>Statistics Property Value</Form.Label>
                            <Form.Control as="select" className="mt-1"
                                onChange={this.onValueChange.bind(this)}
                                value={config.source.sub ? config.source.sub : "-1"}>
                                <option value="-1">Select stat value</option>
                                {this.getValueOptionsForType(config.source).map((option: string, i: number) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        :''
                    }
                    
                    <div className="mt-1">
                        <Button variant="warning" disabled={!this.state.isConfigValid} onClick={this.onSave.bind(this)}>Save</Button>
                        <Button variant="link" onClick={this.onCancel.bind(this)}>cancel</Button>
                    </div>
                </div>
            : ''
        );
    }

    onItemChange(e: any) {
        const measure: IMeasure = {
            source: {
                item: e.target.value === "-1" ? null : this.options[e.target.value].item,
                sub: null
            }
        };

        this.setState({
            isConfigValid: this.isConfigValid(measure),
            config: measure
        });
    }

    onValueChange(e: any) {
        const measure: IMeasure = {
            source: {
                item: this.state.config.source.item,
                sub: e.target.value
            }
        };

        this.setState({
            isConfigValid: this.isConfigValid(measure),
            config: measure
        });
    }

    onClear(e: any) {
        this.setState({
            current: null
        },() => {
            this.props.onUpdate(this.state.current);
        });
    }

    onShow(e: any) {    
        const measure: IMeasure = this.state.current ? { source: {...this.state.current.source}} : { source: { item: null, sub: null }};
    
        this.setState({
            showConfig: true,
            isConfigValid: this.isConfigValid(this.state.current),
            config: measure
        });
    }

    onCancel(e: any) {
        this.setState({
            showConfig: false,
            config: {
                source: {
                    item: null,
                    sub: null
                }
            }
        });
    } 

    onSave(e: any) {
        const measure: IMeasure = this.state.config ? { source: {...this.state.config.source}} : { source: { item: null, sub: null }};
        if(measure.source.sub == null) {
            measure.source.sub = 'value';
        }

        this.setState({
            showConfig: false,
            current: measure
        }, () => {
            this.props.onUpdate(this.state.current);
        });
    }

    isConfigValid(measure: IMeasure | null) {
        const isItemValid = measure != null && measure.source.item != null;
        let isSubItemValid = true;

        if(measure != null && measure.source.item != null && isStats(measure.source.item)) {
            isSubItemValid = measure.source.sub != null;
        }

        return isItemValid && isSubItemValid;
    }

    getValueOptionsForType(item: IItem) {
        if(isStats(item.item)) {
            const property = item.item as Property;
            let propertyStats = property.stats;
            if(item.item?.hasDate){ 
                propertyStats.push('time');
                propertyStats.push('date');
                propertyStats.push('value');
            }
            return propertyStats;
        }
        if(item.item) {
            if(item.item.hasDate){ 
                return ['time', 'date', 'value']
            }
        }
        return [];
    }

    getMeasureDescription(item: IItem): string {
        if(item != null && item.item != null) {
            if(isProperty(item.item)) {
                const property = item.item as Property;
                return `property (${property.type}): ${property.name}`;
            }
        }

        return 'unknown';
    }

    getMeasureSummary(measure: IMeasure): string {
        if(measure.source.item != null) {
            let itemStr = '';
            if(isProperty(measure.source.item)) {
                const property = measure.source.item as Property;
                const subItemStr = measure.source.sub != null ? `[${measure.source.sub}]` : '';
                itemStr = `property (${property.type}): ${property.name}${subItemStr}`;
            }

            return `${itemStr}`;
        }

        return 'configure measure';
    }

    getItemDescription(item: IItem): string {
        if(item != null && item.item != null) {
            if(isEntity(item.item)) {
                const entity = item.item as Entity;
                return `entity: ${entity.name}`;
            } else if(isProperty(item.item)) {
                const property = item.item as Property;
                return `property (${property.type}): ${property.name}`;
            }
        }

        return 'unknown';
    }
}

export default MeasurePropertyCtrl;